import { cn } from '@workspace/ui/lib/utils';

export interface ProductColorsProps {
  /**
   * List of hex color values to render as overlapping dots.
   */
  colors: string[];
  /**
   * Maximum number of color dots rendered before collapsing the rest into a "+N" pill.
   * @default 3
   */
  maxVisible?: number;
  className?: string;
}

const DEFAULT_MAX_VISIBLE = 3;

/**
 * Overlapping color swatches for a product card.
 * Renders up to `maxVisible` color dots; any extra colors collapse into a "+N" pill.
 */
const ProductColors = ({
  colors,
  maxVisible = DEFAULT_MAX_VISIBLE,
  className,
}: ProductColorsProps) => {
  if (!colors.length) {
    return null;
  }

  const visible = colors.slice(0, maxVisible);
  const remaining = colors.length - visible.length;

  return (
    <div className={cn('flex items-center pr-[7px]', className)}>
      {visible.map((color) => (
        <span
          key={color}
          className="mr-[-7px] size-4 shrink-0 rounded-full border border-graphic-regular-white"
          style={{ backgroundColor: color }}
        />
      ))}
      {remaining > 0 && (
        <span className="-mr-[7px] flex size-4 shrink-0 items-center justify-center rounded-full border border-graphic-regular-white bg-graphic-muted-gray-warm">
          <span className="font-(family-name:--font-text) font-medium text-[9px] text-graphic-regular-white leading-3 tracking-[-0.09px]">
            +{remaining > 9 ? '9' : remaining}
          </span>
        </span>
      )}
    </div>
  );
};

export default ProductColors;
